// Utility to open a custom scheme URL (deep link) and optionally fall back to a web / store URL if the app isn't installed.
// USAGE:
//   import { openDeepLink } from "../utils/deepLink";
//   openDeepLink({
//     schemeUrl: "tweezers://home",
//     fallbackUrls: {
//       ios: "https://apps.apple.com/app/idXXXXXXXX",
//       android: "https://play.google.com/store/apps/details?id=com.yourcompany.tweezers",
//       desktop: "https://play.google.com/store/apps/details?id=com.yourcompany.tweezers"
//     }
//   });
//
// The logic attempts to navigate to the custom scheme. If the browser can't handle it (meaning the
// native app isn't installed), after a short delay it redirects to the fallback URL.

// Detect basic platform
const getPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  return "desktop";
};

export const openDeepLink = ({ schemeUrl, fallbackUrls = {}, timeout = 1200 }) => {
  if (typeof window === "undefined") return;

  // Resolve platform-specific fallback
  const platform = getPlatform();
  const fallbackUrl = typeof fallbackUrls === "string" ? fallbackUrls : fallbackUrls[platform];

  // Create an invisible iframe — this pattern works on a wide range of browsers / OSes.
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = schemeUrl;
  document.body.appendChild(iframe);

  // After timeout, assume the deep link failed and go to the fallback (if provided)
  const timer = setTimeout(() => {
    if (fallbackUrl) {
      window.location.href = fallbackUrl;
    }
  }, timeout);

  // If the page gets hidden (user left the browser for the app), cancel the fallback redirect
  const onVisibilityChange = () => {
    if (document.hidden) {
      clearTimeout(timer);
      cleanup();
    }
  };

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
};
